import { defineStore, type _GettersTree } from 'pinia';
import type { LoginCredentialsType } from '@/types/LoginCredentialsType';
import type { UserType } from '@/types/UserType';
import type {
    AuthGettersType, AuthActionsType, AuthStateType,
} from '@/types/stores/AuthStoreTypes';
import type { ProfileUpdateType } from '@/types/ProfileUpdateType';
import { handleRequest, useSplashScreen } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import type { AxiosResponse } from 'axios';
import router from '@/plugins/router';
import { AuthRoutes, SharedRoutes } from '@/types/enums/RouteNameEnums';
import type { AuthTokenType } from '@/types/AuthTokenType';
import { nextTick } from 'vue';
import { setI18nLanguage, type MessageLanguages } from '@plugins/i18n';
import { identifyUserInPostHog } from '@plugins/posthog/usePostHog';

export const useAuthStore = defineStore<
    'auth',
    AuthStateType,
    AuthGettersType & _GettersTree<AuthStateType>,
    AuthActionsType
>('auth', {
    state: (): AuthStateType => ({
        user: null,
        authToken: null,
        isLoading: false,
        returnUrl: null,
        isRefreshing: false,
        isResetPasswordEmailLoading: false,
        isResetPasswordLoading: false,
        isImpersonating: false,
        originalUser: null,
        originalAuthToken: null,
    }),
    getters: {
        isAuthenticated: (state: AuthStateType): boolean => !!state.user && !!state.authToken,
        userInitials: (state: AuthStateType): string => {
            const { first_name = '', last_name = '' } = state.user || {};

            return (first_name[0] || '') + (last_name[0] || '');
        },
    },
    actions: {
        saveAuthToken(tokenData: AuthTokenType): void {
            this.authToken = tokenData;
            this.isImpersonating = tokenData.impersonated || false;
            if (this.authToken) {
                const expiresAt = Math.floor((Date.now() + this.authToken.expires_in * 1000) / 1000);
                this.authToken.expires_in = expiresAt;
                if (!this.isImpersonating) {
                    const refreshExpiresAt = Math.floor((Date.now() + 7 * 24 * 60 * 60 * 1000) / 1000);
                    this.authToken.refresh_expires_in = refreshExpiresAt;
                }
                localStorage.setItem('auth_token', JSON.stringify(this.authToken));
                localStorage.setItem('is_impersonating', JSON.stringify(this.isImpersonating));
                if (this.originalUser) {
                    localStorage.setItem('original_user', JSON.stringify(this.originalUser));
                }
                if (this.originalAuthToken) {
                    localStorage.setItem('original_auth_token', JSON.stringify(this.originalAuthToken));
                }
            }
        },
        async login(credentials: LoginCredentialsType): Promise<void> {
            const { triggerSplashAfterLogin } = useSplashScreen();
            this.isLoading = true;
            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: '/auth/login',
                data: credentials,
                onSuccess: async (response: AxiosResponse<AuthTokenType>) => {
                    this.saveAuthToken(response.data);
                    await this.fetchCurrentUser();

                    triggerSplashAfterLogin();
                    router.push(this.returnUrl || { name: SharedRoutes.Home });
                },
            });
            this.isLoading = false;
        },
        async fetchCurrentUser(): Promise<void> {
            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint: '/auth/me',
                onSuccess: async (response: AxiosResponse<UserType>) => {
                    this.user = response.data;

                    localStorage.setItem('user_data', JSON.stringify(this.user));

                    if (this.user?.preferences?.language) {
                        const userLocale = this.user.preferences.language as MessageLanguages;
                        localStorage.setItem('buylo-locale', userLocale);

                        // Aktualizovat i18n locale po načtení uživatele
                        await setI18nLanguage(userLocale);
                    }

                    identifyUserInPostHog(this.user.id, this.user);
                },
            });
        },
        async logout(redirect: boolean = true): Promise<void> {
            this.isLoading = true;
            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: '/auth/logout',
                onSuccess: () => {
                    this.clearSession();
                    if (redirect) {
                        router.push({ name: AuthRoutes.Login });
                    }
                },
                canShowSnackbar: false,
            });
            this.isLoading = false;
        },
        isTokenValid(): boolean {
            const authToken = localStorage.getItem('auth_token');
            if (!authToken) {
                return false;
            }

            try {
                const tokenData: AuthTokenType = JSON.parse(authToken);

                if (tokenData.impersonated) {
                    return !!tokenData.access_token;
                }

                if (tokenData.refresh_expires_in) {
                    const now = Math.floor(Date.now() / 1000);
                    if (now > tokenData.refresh_expires_in) {
                        return false;
                    }
                }

                return !!tokenData.refresh_token;
            } catch {
                return false;
            }
        },
        isAccessTokenExpired(): boolean {
            if (!this.authToken) {
                return true;
            }
            const currentTime = Math.floor(Date.now() / 1000);
            const expiresIn = Number(this.authToken.expires_in);

            return currentTime > expiresIn;
        },
        async checkAuth() {
            const savedToken = localStorage.getItem('auth_token');
            const savedUser = localStorage.getItem('user_data');
            const savedIsImpersonating = localStorage.getItem('is_impersonating');
            const savedOriginalUser = localStorage.getItem('original_user');
            const savedOriginalAuthToken = localStorage.getItem('original_auth_token');
            if (savedToken && savedUser && this.isTokenValid()) {
                this.authToken = JSON.parse(savedToken);
                this.user = JSON.parse(savedUser);
                this.isImpersonating = savedIsImpersonating ? JSON.parse(savedIsImpersonating) : false;
                this.originalUser = savedOriginalUser ? JSON.parse(savedOriginalUser) : null;
                this.originalAuthToken = savedOriginalAuthToken ? JSON.parse(savedOriginalAuthToken) : null;

                // Aktualizovat locale pokud je v user preferences
                if (this.user?.preferences?.language) {
                    const userLocale = this.user.preferences.language as MessageLanguages;
                    await setI18nLanguage(userLocale).catch(console.error);
                }

                if (this.user) {
                    identifyUserInPostHog(this.user.id, this.user);
                }
            } else {
                this.clearSession();
            }
        },
        async refreshToken(): Promise<void> {
            if (this.isRefreshing || !this.authToken?.refresh_token || this.isImpersonating) {
                return;
            }

            this.isRefreshing = true;

            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: '/auth/refresh',
                data: { refresh_token: this.authToken.refresh_token },
                onSuccess: (response: AxiosResponse<AuthTokenType>) => {
                    this.saveAuthToken(response.data);
                    this.isRefreshing = false;
                },
                onError: () => {
                    this.isRefreshing = false;
                    this.clearSession();
                },
            });
        },
        async updateProfile(profileData: ProfileUpdateType) {
            this.isLoading = true;
            await handleRequest({
                method: RequestMethodEnum.Put,
                endpoint: '/auth/me',
                data: profileData,
                onSuccess: async (response: AxiosResponse<UserType>) => {
                    this.user = response.data;
                    localStorage.setItem('user_data', JSON.stringify(this.user));

                    // Aktualizovat locale pokud se změnil jazyk
                    if (this.user?.preferences?.language) {
                        const userLocale = this.user.preferences.language as MessageLanguages;
                        localStorage.setItem('buylo-locale', userLocale);
                        await setI18nLanguage(userLocale);
                    }
                },
            });
            this.isLoading = false;
        },
        clearSession() {
            const { resetSplash } = useSplashScreen();
            this.user = null;
            this.authToken = null;
            this.returnUrl = null;
            this.isImpersonating = false;
            this.originalUser = null;
            this.originalAuthToken = null;
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            localStorage.removeItem('is_impersonating');
            localStorage.removeItem('original_user');
            localStorage.removeItem('original_auth_token');
            resetSplash();
        },
        async sendPasswordResetEmail(email: string): Promise<void> {
            this.isResetPasswordEmailLoading = true;
            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: '/auth/forgot-password',
                data: { email },
                successMessageKey: 'messages.emailWithLinkToResetPasswordSent',
                successMessageParams: { email },
            });
            this.isResetPasswordEmailLoading = false;
        },
        async resetPassword(
            email: string,
            token: string,
            password: string,
            password_confirmation: string,
            isCreate: boolean = false,
            broker: string = 'users',
        ): Promise<void> {
            this.isResetPasswordLoading = true;
            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: isCreate ? '/auth/create-password' : '/auth/reset-password',
                successMessageKey: isCreate ? 'messages.createPasswordSuccess' : 'messages.resetPasswordSuccess',
                data: { email, password, password_confirmation, token, broker },
                onSuccess: () => {
                    router.push({ name: AuthRoutes.Login });
                },
            });
            this.isResetPasswordLoading = false;
        },
        async sendAdminResetPasswordEmail(userId: number): Promise<void> {
            this.isResetPasswordEmailLoading = true;
            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: `/users/${userId}/send-reset-password`,
                successMessageKey: 'messages.emailWithLinkToResetPasswordSent',
            });
            this.isResetPasswordEmailLoading = false;
        },
        async loginAsUser(userId: number): Promise<void> {
            this.isLoading = true;
            if (!this.isImpersonating && this.user && this.authToken) {
                this.originalUser = this.user;
                this.originalAuthToken = { ...this.authToken };
            }
            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: `/impersonate/login/${userId}`,
                onSuccess: async (response: AxiosResponse<AuthTokenType & { user: UserType }>) => {
                    const tokenData = {
                        ...response.data,
                        refresh_token: '',
                        impersonated: true,
                    };
                    this.saveAuthToken(tokenData);
                    this.user = response.data.user;
                    localStorage.setItem('user_data', JSON.stringify(this.user));
                },
            });
            this.isLoading = false;
            if (this.isAuthenticated) {
                await nextTick();
                await router.replace({ name: SharedRoutes.Home });
            }
        },
        async returnToSelf(): Promise<void> {
            this.isLoading = true;
            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: '/impersonate/return',
                onSuccess: async () => {
                    if (this.originalAuthToken && this.originalUser) {
                        this.authToken = { ...this.originalAuthToken };
                        this.user = { ...this.originalUser };
                        this.isImpersonating = false;
                        this.originalUser = null;
                        this.originalAuthToken = null;
                        localStorage.setItem('auth_token', JSON.stringify(this.authToken));
                        localStorage.setItem('user_data', JSON.stringify(this.user));
                        localStorage.removeItem('is_impersonating');
                        localStorage.removeItem('original_user');
                        localStorage.removeItem('original_auth_token');
                    }
                },
                onError: () => {
                    this.clearSession();
                    throw new Error('Failed to return to self');
                },
            });
            this.isLoading = false;
            if (this.isAuthenticated) {
                await nextTick();
                await router.replace({ name: SharedRoutes.Users });
            }
        },
    },
});
